import { useEffect, useRef, useState } from 'react'

/**
 * Renders a PDF to canvases with PDF.js.
 *
 * WHY NOT AN IFRAME, which is far less code:
 * mobile browsers refuse to render a PDF inside one — Chrome on Android and
 * Safari on iOS alike. They show a blank or broken-document box with no error.
 * Desktop Chrome renders it fine, which is exactly why the iframe passed testing
 * and failed in the shop. Drawing the pages ourselves works everywhere.
 *
 * WHY A FULL-SCREEN MODE RATHER THAN INLINE ZOOM:
 * the first attempt zoomed in place, widened the viewer past its column, and
 * made the zoom bar and buttons `position: sticky`. Those float ON TOP of the
 * page content — so on both phone and desktop the controls sat over the
 * scorecard, and on a big monitor the whole thing appeared to take over the
 * screen. Expanding into a proper overlay fixes that structurally: the toolbar
 * is a flex row and the pages are a scrolling flex child, so content physically
 * cannot slide underneath the controls.
 *
 * PDF.js is imported dynamically so it only downloads when someone opens the
 * Scorecard tab.
 */

interface Props {
  /** Raw PDF bytes. Handed straight to PDF.js — see the note in the effect. */
  bytes: Uint8Array
  /** Blob URL, used by the open-in-browser and download buttons. */
  url: string
  fileName: string
}

type State = 'loading' | 'ready' | 'failed'

const ZOOM_STEPS = [1, 1.5, 2, 3, 4] as const

export default function PdfView({ bytes, url, fileName }: Props) {
  const host = useRef<HTMLDivElement>(null)
  const [state, setState] = useState<State>('loading')
  const [pages, setPages] = useState(0)
  const [detail, setDetail] = useState<string | null>(null)
  const [expanded, setExpanded] = useState(false)
  const [zoomIdx, setZoomIdx] = useState(0)

  useEffect(() => {
    let cancelled = false
    const container = host.current
    if (!container) return

    setState('loading')
    setDetail(null)
    container.replaceChildren()

    void (async () => {
      try {
        // ⚠️ THE **LEGACY** BUILD, deliberately. The default build of PDF.js 6
        // calls `Map.prototype.getOrInsertComputed`, a very new JS method that
        // older Safari does not have — one of the shop's team hit exactly this
        // on an older iPhone: "getOrInsertComputed is not a function".
        // Reproduced in a modern browser by deleting the method, and confirmed
        // fixed the same way. The legacy build bundles core-js polyfills for
        // `get-or-insert` and `get-or-insert-computed` in BOTH the main bundle
        // and the worker; the default build has no polyfill at all.
        // Costs ~58 KB on the main chunk and ~50 KB on the worker. Do not
        // "optimise" this back to 'pdfjs-dist' — it silently breaks old phones,
        // and the shop floor is exactly where old phones are.
        const pdfjs = await import('pdfjs-dist/legacy/build/pdf.mjs')
        // The worker must come from our own origin for `script-src 'self'`.
        // Vite rewrites this ?url import to a hashed asset at build time.
        // Legacy worker to match — the polyfills are needed on both sides.
        const workerUrl = (await import('pdfjs-dist/legacy/build/pdf.worker.min.mjs?url')).default
        pdfjs.GlobalWorkerOptions.workerSrc = workerUrl

        // Pass the DATA, not the blob URL. Given a URL, PDF.js fetches it, and
        // `connect-src` does not allow blob: — which surfaced as
        // "Unexpected server response (0)". A fresh copy each time because
        // PDF.js may detach the buffer it is given, which breaks re-renders.
        const doc = await pdfjs.getDocument({ data: new Uint8Array(bytes) }).promise
        if (cancelled) return
        setPages(doc.numPages)

        // A scorecard is a dense wide table, so people zoom in — and a canvas
        // rendered only at screen width turns to mush the moment they do.
        // Lowered for long documents: every page is a full bitmap in memory,
        // and 2400px wide is roughly 15 MB each.
        const floorPx = doc.numPages > 5 ? 1600 : 2400

        for (let n = 1; n <= doc.numPages; n++) {
          if (cancelled) return
          const page = await doc.getPage(n)

          const unscaled = page.getViewport({ scale: 1 })
          const cssWidth = container.clientWidth || 600
          const dpr = Math.min(window.devicePixelRatio || 1, 2)
          const targetPixels = Math.max(cssWidth * dpr, floorPx)
          const viewport = page.getViewport({ scale: targetPixels / unscaled.width })

          const canvas = document.createElement('canvas')
          canvas.className = 'pdf__page'
          canvas.width = Math.floor(viewport.width)
          canvas.height = Math.floor(viewport.height)

          const ctx = canvas.getContext('2d')
          if (!ctx) throw new Error('Could not get a drawing context.')

          container.appendChild(canvas)
          await page.render({ canvas, canvasContext: ctx, viewport }).promise
        }

        if (!cancelled) setState('ready')
      } catch (err) {
        if (cancelled) return
        setState('failed')
        setDetail(err instanceof Error ? err.message : String(err))
      }
    })()

    return () => {
      cancelled = true
    }
  }, [bytes])

  // Zoom is CSS width on the existing canvases — no re-render needed, the
  // bitmap is already far larger than the display. Only meaningful while
  // expanded; inline always sits at fit-width so it never overflows its column.
  const zoom = expanded ? (ZOOM_STEPS[zoomIdx] ?? 1) : 1
  useEffect(() => {
    const container = host.current
    if (!container) return
    for (const c of Array.from(container.querySelectorAll('canvas'))) {
      c.style.width = `${zoom * 100}%`
    }
  }, [zoom, state, pages])

  // Escape closes, and the page behind must not scroll while the overlay is up.
  useEffect(() => {
    if (!expanded) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setExpanded(false)
    }
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prev
      window.removeEventListener('keydown', onKey)
    }
  }, [expanded])

  return (
    <div className={'pdf' + (expanded ? ' pdf--expanded' : '')}>
      {expanded && (
        <div className="pdf__toolbar">
          <div className="pdf__zoom" role="group" aria-label="Zoom">
            <button
              type="button"
              className="pdf__zoombtn"
              aria-label="Zoom out"
              disabled={zoomIdx === 0}
              onClick={() => setZoomIdx((i) => Math.max(0, i - 1))}
            >
              −
            </button>
            <span className="pdf__level">{Math.round(zoom * 100)}%</span>
            <button
              type="button"
              className="pdf__zoombtn"
              aria-label="Zoom in"
              disabled={zoomIdx === ZOOM_STEPS.length - 1}
              onClick={() => setZoomIdx((i) => Math.min(ZOOM_STEPS.length - 1, i + 1))}
            >
              +
            </button>
          </div>
          <button type="button" className="btn" onClick={() => setExpanded(false)}>
            Close
          </button>
        </div>
      )}

      {state === 'loading' && <p className="muted pdf__status">Opening the scorecard…</p>}

      {state === 'failed' && (
        <div className="panel panel--flat">
          <p className="muted">
            This scorecard could not be displayed here. Use the buttons below to open it
            in your browser or save it.
          </p>
          {detail && <p className="muted pdf__detail">{detail}</p>}
        </div>
      )}

      <div ref={host} className="pdf__pages" />

      {!expanded && (
        <div className="viewer__actions">
          {state === 'ready' && (
            <button type="button" className="btn" onClick={() => setExpanded(true)}>
              Expand
            </button>
          )}
          {/* Opens the browser's own PDF viewer — full zoom, search and print on
              a desktop. On a phone it hands off to the PDF app or downloads. */}
          <a className="btn btn--quiet" href={url} target="_blank" rel="noopener noreferrer">
            Open in browser
          </a>
          {/* A real download always works, including where opening a blob: URL
              in a new tab is blocked by the browser. */}
          <a className="btn btn--quiet" href={url} download={fileName}>
            Download
          </a>
          {pages > 1 && <span className="muted pdf__count">{pages} pages</span>}
        </div>
      )}
    </div>
  )
}
